import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, CalendarClock, AlertTriangle, CheckCircle2, MessageCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { inputCls } from "../admin/AFields";
import { pickLocalized } from "./localized";

export default function StudioSender() {
  const { base } = useOutletContext();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [greetings, setGreetings] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [waState, setWaState] = useState("");
  const [form, setForm] = useState({ greeting_id: "", sender_profile_id: "", template_id: "", groups: [], scheduled_at: "" });
  const [eligible, setEligible] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/greetings/list").then((r) => setGreetings(r.data.filter((g) => g.status !== "archived"))).catch(() => {});
    api.get("/studio/orgs").then((r) => setOrgs(r.data.filter((o) => o.enabled !== false))).catch(() => {});
    api.get("/wa/templates").then((r) => setTemplates(r.data.filter((t) => t.status === "approved"))).catch(() => {});
    api.get("/greetings/groups").then((r) => setGroups(r.data)).catch(() => {});
    api.get("/wa/status").then((r) => setWaState(r.data.state)).catch(() => {});
  }, []);

  useEffect(() => {
    const gid = params.get("greeting_id");
    if (gid) setForm((f) => ({ ...f, greeting_id: gid }));
  }, [params]);

  useEffect(() => {
    const g = form.groups.join(",");
    api.get(`/wa/recipient-count?groups=${encodeURIComponent(g)}`).then((r) => setEligible(r.data.eligible)).catch(() => setEligible(null));
  }, [form.groups]);

  const greeting = useMemo(() => greetings.find((g) => g.id === form.greeting_id), [greetings, form.greeting_id]);
  const org = useMemo(() => orgs.find((o) => o.id === form.sender_profile_id) || orgs.find((o) => o.is_default) || {}, [orgs, form.sender_profile_id]);
  const template = useMemo(() => templates.find((t) => t.id === form.template_id), [templates, form.template_id]);

  const toggleGroup = (g) => setForm((f) => ({ ...f, groups: f.groups.includes(g) ? f.groups.filter((x) => x !== g) : [...f.groups, g] }));

  const warnings = [
    [waState === "NOT CONFIGURED", "WhatsApp is NOT CONFIGURED — connect AiSensy first (you can still schedule the campaign)."],
    [templates.length === 0, "No APPROVED WhatsApp template registered — template approval is mandatory."],
    [eligible === 0, "No eligible recipients in the selected group(s)."],
    [greeting && !(greeting.whatsapp_message || greeting.message), "This greeting has no WhatsApp message text."],
  ].filter(([x]) => x);

  const canSend = form.greeting_id && form.template_id && eligible > 0;

  const submit = async (sendNow) => {
    setBusy(sendNow ? "now" : "sched");
    try {
      const body = { greeting_id: form.greeting_id, sender_profile_id: form.sender_profile_id || org.id,
                     template_id: form.template_id, groups: form.groups,
                     scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : "",
                     send_now: sendNow };
      const r = await api.post("/wa/campaigns", body);
      toast.success(sendNow ? `Campaign sending — ${r.data.recipient_count} recipients` : `Campaign scheduled — ${r.data.recipient_count} recipients`);
      navigate(`${base}/campaigns`);
    } catch (e) {
      const msg = errMsg(e);
      if (e?.response?.status === 409) {
        toast.error(msg);
      } else toast.error(msg);
    } finally { setBusy(""); }
  };

  const sampleText = pickLocalized(greeting?.whatsapp_message || greeting?.message).replace("{{name}}", "Amit Kumar")
    .replace("{{occasion}}", pickLocalized(greeting?.occasion)).replace("{{organization}}", org.organisation || org.name || "")
    .replace("{{sender}}", org.sender_name || org.name || "").replace("{{date}}", greeting?.date || "");

  return (
    <div data-testid="studio-sender">
      <SEO title="WhatsApp Sender" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Send on WhatsApp</h1>
      <p className="mt-1 text-sm text-slate-500">Greeting → Recipients → Template → Preview → Send. Nothing sends without this page.</p>

      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">1 · Greeting</label>
            <select value={form.greeting_id} onChange={(e) => setForm({ ...form, greeting_id: e.target.value })} className={inputCls} data-testid="sender-greeting">
              <option value="">Select greeting…</option>
              {greetings.map((g) => <option key={g.id} value={g.id}>{pickLocalized(g.title)} · {pickLocalized(g.occasion) || g.date}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">2 · Sender Identity</label>
            <select value={form.sender_profile_id || org.id || ""} onChange={(e) => setForm({ ...form, sender_profile_id: e.target.value })} className={inputCls} data-testid="sender-profile">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">3 · Approved Template</label>
            <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} className={inputCls} data-testid="sender-template">
              <option value="">Select template…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.language})</option>)}
            </select>
            {templates.length === 0 && <p className="mt-1.5 text-xs font-semibold text-saffron-600">No approved templates — register one in <Link to={`${base}/whatsapp`} className="underline">WhatsApp → Templates</Link> after it is approved in AiSensy.</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">4 · Contact Groups (blank = all eligible)</label>
            <div className="flex flex-wrap gap-2" data-testid="sender-groups">
              {groups.map((g) => (
                <button key={g.id} type="button" onClick={() => toggleGroup(g.name)} data-testid={`sender-group-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${form.groups.includes(g.name) ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>
                  {g.name}
                </button>
              ))}
              {groups.length === 0 && <p className="text-xs text-slate-400">No groups yet — contacts can still be targeted as “all eligible”.</p>}
            </div>
            <p className="mt-2 text-xs font-bold text-navy" data-testid="sender-eligible">{eligible === null ? "…" : `${eligible} eligible recipients`}{form.groups.length === 0 ? " (all contacts)" : ""}</p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">5 · Schedule (optional)</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className={inputCls} data-testid="sender-schedule" />
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-lg border border-gray-200 bg-white p-6" data-testid="sender-preview">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Preview (sample recipient: Amit Kumar)</p>
            {greeting ? (
              <div className="mt-3">
                {greeting.image && <img src={greeting.image} alt="" className="aspect-square w-full max-w-[280px] rounded-lg object-cover" />}
                <p className="mt-3 text-sm font-bold text-navy">{pickLocalized(greeting.title)}</p>
                <p className="mt-1 whitespace-pre-line rounded-lg bg-[#E7F6E9] p-3 text-sm text-slate-700">{sampleText || "—"}</p>
                <div className="mt-3 space-y-1 text-xs text-slate-500">
                  <p>Sender: <span className="font-bold text-navy">{org.name}</span></p>
                  <p>Template: <span className="font-bold text-navy">{template ? `${template.name} (approved)` : "—"}</span></p>
                  <p>Groups: <span className="font-bold text-navy">{form.groups.join(", ") || "All eligible contacts"}</span></p>
                  <p>Schedule: <span className="font-bold text-navy">{form.scheduled_at ? new Date(form.scheduled_at).toLocaleString() : "Send now"}</span></p>
                </div>
              </div>
            ) : <p className="mt-4 text-sm text-slate-400">Select a greeting to preview.</p>}
          </div>

          {warnings.length > 0 && (
            <div className="space-y-2" data-testid="sender-warnings">
              {warnings.map(([, w], i) => (
                <p key={i} className="flex items-center gap-2 rounded-lg bg-saffron-100 px-4 py-2.5 text-xs font-semibold text-saffron-600"><AlertTriangle size={14} /> {w}</p>
              ))}
            </div>
          )}
          {canSend && warnings.length === 0 && (
            <p className="flex items-center gap-2 rounded-lg bg-brandgreen-100 px-4 py-2.5 text-xs font-bold text-brandgreen"><CheckCircle2 size={14} /> Ready — personalization validated, no unresolved variables.</p>
          )}

          <div className="flex flex-wrap gap-3">
            <button onClick={() => submit(true)} disabled={busy !== "" || !canSend} className="btn-primary !rounded-md !px-6 !py-3 text-sm disabled:opacity-50" data-testid="sender-send-now">
              {busy === "now" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send Now
            </button>
            <button onClick={() => submit(false)} disabled={busy !== "" || !canSend || !form.scheduled_at} className="btn-dark !rounded-md !px-6 !py-3 text-sm disabled:opacity-50" data-testid="sender-schedule-btn">
              {busy === "sched" ? <Loader2 size={15} className="animate-spin" /> : <CalendarClock size={15} />} Schedule
            </button>
          </div>
          {waState === "NOT CONFIGURED" && (
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-500"><MessageCircle size={13} /> Campaigns will be created as drafts until WhatsApp is connected in <Link to={`${base}/whatsapp`} className="font-bold text-saffron-600 underline">WhatsApp settings</Link>.</p>
          )}
        </div>
      </div>
    </div>
  );
}
